import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchAxios } from "../API/fetchAxios";
import { getIbAccountQueryParams } from "../Constants/accountTypes";
import { useToast } from "./toast";
import { normalizeTransactionsData } from "./Normalizers";
import { useValidateErrorResponse } from "./useValidateErrorResponse";

/** BaaS `ListAccountStatementsQueryValidator`: InclusiveBetween(1, 100). */
export const BAAS_STATEMENT_PAGE_SIZE = 100;
/** Max rows Extrato aggregates across BaaS pages (former single-request page_size). */
export const MAX_STATEMENT_ITEMS = 5000;

const { CancelToken } = axios;

/** Never forward to BaaS `GET /v1/accounts/{id}/statements`. */
const CLIENT_ONLY_KEYS = new Set([
  "fetch_all",
  "max_items",
  "is_paged_result",
  "start_amount",
  "end_amount",
  "sub_account_id",
  "is_refund",
]);

const toBaasQuery = (params = {}, { page, pageSize }) => {
  const query = { ...getIbAccountQueryParams(params.account_id ?? params.accountId) };
  Object.entries(params).forEach(([key, value]) => {
    if (key === "account_id" || key === "accountId") return;
    if (CLIENT_ONLY_KEYS.has(key)) return;
    if (value === "" || value === null || value === undefined) return;
    if (key === "page" || key === "page_size" || key === "limit") return;
    // Legacy IB sent type=all for "no filter"; BaaS matches Type == "ALL" literally.
    if (key === "type" && String(value).toLowerCase() === "all") return;
    query[key] = value;
  });
  query.page = page;
  query.page_size = pageSize;
  return query;
};

const resolvePageCount = (payload = {}) => {
  if (Number.isFinite(Number(payload.pageCount)) && Number(payload.pageCount) > 0) {
    return Number(payload.pageCount);
  }
  const total = Number(payload.total);
  const pageSize = Number(payload.page_size ?? payload.pageSize);
  if (Number.isFinite(total) && total >= 0 && Number.isFinite(pageSize) && pageSize > 0) {
    return Math.max(1, Math.ceil(total / pageSize));
  }
  return 0;
};

const formatStatementErrorToast = (body = {}) => {
  const validationParts = body.errors
    ? Object.values(body.errors)
        .flat()
        .filter(Boolean)
    : [];
  return {
    title: body.information || body.title || "Erro",
    description: body.description || validationParts.join(" ") || "",
    type: body.isError === false ? "warning" : "error",
  };
};

/**
 * Loads IB account statements via MobileService `/v1/ib/accounts/statements`.
 * When `fetch_all` is set or `page_size` > 100, pages BaaS in chunks of 100
 * until exhausted or `max_items` (default {@link MAX_STATEMENT_ITEMS}).
 */
export const fetchAccountStatements = async (params = {}, { cancelToken } = {}) => {
  const requestedPageSize = Number(params.page_size ?? params.limit);
  const wantsAll =
    Boolean(params.fetch_all) ||
    (Number.isFinite(requestedPageSize) && requestedPageSize > BAAS_STATEMENT_PAGE_SIZE);

  const maxItems = Math.min(
    MAX_STATEMENT_ITEMS,
    Math.max(
      1,
      Number(params.max_items) ||
        (wantsAll
          ? Number.isFinite(requestedPageSize)
            ? requestedPageSize
            : MAX_STATEMENT_ITEMS
          : BAAS_STATEMENT_PAGE_SIZE),
    ),
  );

  const pageSize = wantsAll
    ? BAAS_STATEMENT_PAGE_SIZE
    : Math.min(
        BAAS_STATEMENT_PAGE_SIZE,
        Math.max(1, Number.isFinite(requestedPageSize) ? requestedPageSize : BAAS_STATEMENT_PAGE_SIZE),
      );

  if (!wantsAll) {
    const page = Math.max(1, Number(params.page) || 1);
    return fetchAxios({
      verb: "GET",
      context: "accountStatement",
      queryParameters: toBaasQuery(params, { page, pageSize }),
      cancelToken,
    });
  }

  const items = [];
  let total = Infinity;
  let page = 1;
  let lastPayload = {};
  const maxPages = Math.ceil(maxItems / pageSize);

  while (page <= maxPages && items.length < total && items.length < maxItems) {
    const response = await fetchAxios({
      verb: "GET",
      context: "accountStatement",
      queryParameters: toBaasQuery(params, { page, pageSize }),
      cancelToken,
    });
    const payload = response?.data ?? {};
    const batch = Array.isArray(payload.items) ? payload.items : [];
    total = Number(payload.total ?? batch.length);
    items.push(...batch);
    lastPayload = payload;
    if (batch.length < pageSize) break;
    page += 1;
  }

  return {
    data: {
      ...lastPayload,
      items: items.slice(0, maxItems),
      total: Number(lastPayload.total ?? items.length),
      page: 1,
      page_size: pageSize,
    },
  };
};

export const useTransactionsData = () => {
  const { addToast } = useToast();
  const { validateError } = useValidateErrorResponse();
  const isMountedRef = useRef(true);
  const cancelSourcesRef = useRef([]);

  const [requestLoading, setRequestLoading] = useState({ GET: null });
  const [requestSuccess, setRequestSuccess] = useState({ GET: null });
  const [requestData, setRequestData] = useState({});

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cancelSourcesRef.current.forEach((source) => {
        source.cancel("Component unmounted");
      });
      cancelSourcesRef.current = [];
    };
  }, []);

  const reset = useCallback(() => {
    setRequestData({});
    setRequestLoading({ GET: null });
    setRequestSuccess({ GET: null });
  }, []);

  const handleGET = useCallback(
    (params) => {
      if (requestLoading.GET) return;

      setRequestLoading({ GET: true });
      setRequestSuccess({ GET: null });

      const axiosToken = CancelToken.source();
      cancelSourcesRef.current.push(axiosToken);

      const removeCancelSource = () => {
        cancelSourcesRef.current = cancelSourcesRef.current.filter(
          (source) => source !== axiosToken,
        );
      };

      fetchAccountStatements(params, { cancelToken: axiosToken.token })
        .then((values) => {
          removeCancelSource();
          if (!isMountedRef.current) return;
          setRequestData(values);
          setRequestLoading({ GET: false });
          setRequestSuccess({ GET: true });
        })
        .catch((err) => {
          removeCancelSource();
          if (axios.isCancel(err) || !isMountedRef.current) return;
          if (err.response?.data) {
            addToast(formatStatementErrorToast(err.response.data));
          }
          setRequestData({});
          setRequestLoading({ GET: false });
          setRequestSuccess({ GET: false });
          validateError(err?.response);
        });
    },
    [addToast, requestLoading.GET, validateError],
  );

  const state = normalizeTransactionsData(requestData);
  const payload = requestData.data || {};

  const data = {
    ...payload,
    items: state,
    pageCount: resolvePageCount(payload),
  };

  return {
    reset,
    loading: requestLoading,
    state: data,
    success: requestSuccess,
    handleGET,
  };
};
